import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api.js";
import { Dial } from "./chrome.js";

/**
 * **Set up, step 5** — the loop's own dials (23 §autopilot, ADR 0039).
 *
 * Read by anybody in the workspace and changed only by an owner: the loop spends this business's credits and
 * writes to strangers under its name. The page says what this box would actually let it do, rather than offering
 * a switch that would quietly do nothing.
 */

/**
 * The loop's own dials, which used to be a screen of their own.
 *
 * They are five numbers and a switch, and none of them is something a person changes twice a week — so they
 * belong at the bottom of the thing you set up once, not in the tab bar beside the work.
 */
export function Loop({ mayEdit }: { mayEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["customers", "autopilot"], queryFn: () => api.autopilot() });
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.saveAutopilot(patch),
    onSuccess: (r) => { qc.setQueryData(["customers", "autopilot"], r); void qc.invalidateQueries({ queryKey: ["customers", "today"] }); },
  });
  const d = q.data;
  if (!d) return <p className="text-[12.5px] text-faint">…</p>;
  const s = d.switches;
  const set = (patch: Record<string, unknown>) => { if (mayEdit) save.mutate(patch); };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Dial label="Letters a week" value={String(s.lettersPerWeek)} disabled={!mayEdit}
          onChange={(v) => set({ lettersPerWeek: Number(v) || 0 })} />
        <Dial label="Credits a week" value={s.weeklyBudget} disabled={!mayEdit}
          onChange={(v) => set({ weeklyBudget: v.replace(/[^0-9.]/g, "") })} />
        <Dial label="Trying, not earning" value={`${s.exploreShare}%`} disabled={!mayEdit}
          onChange={(v) => set({ exploreShare: Number(v.replace(/[^0-9]/g, "")) || 30 })} />
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">What it may change on its own</span>
        <select
          value={s.learningApplies} disabled={!mayEdit}
          onChange={(e) => set({ learningApplies: e.target.value })}
          className="self-start rounded-lg border border-line-2 bg-page px-2.5 py-1.5 text-[13px] disabled:opacity-50"
        >
          <option value="none">nothing — everything waits for you</option>
          <option value="words">the words, and a market to try</option>
          <option value="all">those, and a narrower group</option>
        </select>
        <span className="text-[11px] text-faint">Retiring a group always waits for you, whatever this says.</span>
      </label>
      <div className="flex flex-wrap items-center gap-2.5 border-t border-line pt-2.5">
        <label className="flex items-center gap-2 text-[12.5px]">
          <input type="checkbox" checked={s.sends} disabled={!mayEdit || !d.maySend} onChange={(e) => set({ sends: e.target.checked })} />
          It may send its own letters
        </label>
        <Dial label="a day, at most" value={String(s.lettersPerDay)} disabled={!mayEdit} small
          onChange={(v) => set({ lettersPerDay: Number(v) || 0 })} />
        <span className={`ml-auto px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${d.maySend ? "bg-accent/10 text-accent" : "bg-line-2 text-ink-2"}`}>
          {d.gate === "rehearsal" ? "rehearsing · they all come to you"
            : d.gate === "allowlist" ? "your own addresses only"
              : d.gate === "on" ? "live — the loop still will not send" : "nothing leaves this box"}
        </span>
      </div>
      <p className="text-[11px] text-faint leading-relaxed">
        It sends only letters it wrote itself, never one you have started editing, and only while this box is
        rehearsing: turning the box live does not hand it a list of companies.
      </p>
    </div>
  );
}
